/**
 * Alarm Templates CRUD Module
 * Handles create, edit, and delete operations for alarm templates
 */

import { t } from '../core/i18n.js';
import { auth } from '../core/auth.js';
import { API_BASE_URL } from '../core/config.js';
import { showError, showSuccess, showConfirmDialog, showAlertDialog } from '../core/ui.js';

// Close modal helper
function closeModal() {
    const modalContainer = document.getElementById('modal');
    if (modalContainer) {
        modalContainer.innerHTML = '';
        modalContainer.classList.add('hidden');
    }
}

// Schedule counter for unique IDs
let scheduleCounter = 0;

/**
 * Show create/edit alarm template modal
 */
export async function showCreateAlarmTemplateModal(existingTemplate = null) {
    const isEditMode = !!existingTemplate;
    
    // Show loading state immediately
    const modal = document.getElementById('modal');
    if (modal) {
        modal.innerHTML = `
            <div class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
                <div class="bg-white rounded-lg shadow-xl p-8">
                    <div class="text-center">
                        <svg class="animate-spin h-8 w-8 text-primary-600 mx-auto mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <p class="text-sm text-gray-600">Loading...</p>
                    </div>
                </div>
            </div>
        `;
        modal.classList.remove('hidden');
    }
    
    // Load users for SMS recipient selection
    const users = await loadUsersForSMS();
    const emailUsers = await loadUsersForEmail();
    
    // Filter to only users with phone numbers
    const usersWithPhone = users.filter(user => user.phone && user.phone.trim());
    const usersWithoutPhone = users.length - usersWithPhone.length;
    const isMissingCheckin = existingTemplate?.alarm_type === 'missing_checkin_report';
    
    const modalHtml = `
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
            <div class="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div class="px-6 py-4 border-b border-gray-200">
                    <h3 class="text-lg font-medium text-gray-900">${isEditMode ? t('alarmTemplates.modal.edit') : t('alarmTemplates.modal.create')}</h3>
                </div>
                <form id="alarm-template-form" class="px-6 py-4">
                    ${isEditMode ? `<input type="hidden" id="template-id" value="${existingTemplate._id}">` : ''}
                    <div class="space-y-4">
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">${t('alarmTemplates.modal.sensorType')}</label>
                            <select id="template-sensor-type" required onchange="toggleAlarmFields()" ${isEditMode ? 'disabled' : ''}
                                    class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 px-3 py-2 ${isEditMode ? 'bg-gray-100' : ''}">
                                <option value="">${t('alarmTemplates.modal.selectSensorType')}</option>
                                <option value="card_reader" ${existingTemplate?.sensor_type === 'card_reader' && !isMissingCheckin ? 'selected' : ''}>${t('alarmTemplates.cardReader')}</option>
                                <option value="temperature" ${existingTemplate?.sensor_type === 'temperature' ? 'selected' : ''}>${t('alarmTemplates.temperature')}</option>
                                <option value="temperature_humidity" ${existingTemplate?.sensor_type === 'temperature_humidity' ? 'selected' : ''}>Temperature/Humidity/Pressure</option>
                                <option value="tracker" ${existingTemplate?.sensor_type === 'tracker' ? 'selected' : ''}>${t('alarmTemplates.tracker')}</option>
                                <option value="missing_checkin" ${isMissingCheckin ? 'selected' : ''}>${t('alarmTemplates.missingCheckin')}</option>
                            </select>
                            <p class="mt-1 text-xs text-gray-500">${isEditMode ? t('alarmTemplates.modal.sensorTypeNoChange') : t('alarmTemplates.modal.sensorTypeHelp')}</p>
                        </div>
                        
                        <div id="alarm-fields-container">
                            <div id="coming-soon-message" class="rounded-md bg-blue-50 p-4">
                                <div class="flex">
                                    <svg class="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                                    </svg>
                                    <div class="ml-3">
                                        <p class="text-sm text-blue-800">${t('alarmTemplates.modal.selectSensorTypePrompt')}</p>
                                    </div>
                                </div>
                            </div>
                            
                            <div id="card-reader-fields" class="hidden space-y-4">
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-1">${t('alarmTemplates.modal.templateName')}</label>
                                    <input type="text" id="template-name" 
                                           class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                                           placeholder="${t('alarmTemplates.modal.templateNamePlaceholder')}">
                                </div>
                                
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-1">${t('alarmTemplates.modal.description')}</label>
                                    <textarea id="template-description" rows="2"
                                              class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                                              placeholder="${t('alarmTemplates.modal.descriptionPlaceholder')}"></textarea>
                                </div>
                                
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-1">${t('alarmTemplates.modal.timezone')}</label>
                                    <input type="text" id="template-timezone" readonly
                                           class="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                                           value="">
                                    <p class="mt-1 text-xs text-gray-500">${t('alarmTemplates.modal.timezoneHelp')}</p>
                                </div>
                                
                                <!-- Time Schedules Section -->
                                <div class="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                    <div class="flex items-center justify-between mb-3">
                                        <label class="block text-sm font-medium text-gray-700">
                                            ${t('alarmTemplates.modal.timeSchedules')} 
                                            <span class="text-xs font-normal text-gray-500">${t('alarmTemplates.modal.schedulesRange')}</span>
                                        </label>
                                        <button type="button" onclick="addSchedule()" id="add-schedule-btn"
                                                class="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1">
                                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                                            </svg>
                                            ${t('alarmTemplates.modal.addSchedule')}
                                        </button>
                                    </div>
                                    
                                    <div id="schedules-container">
                                        <!-- Schedules will be added here -->
                                    </div>
                                </div>
                                
                                <div class="border-t border-gray-200 pt-4">
                                    <label class="flex items-center">
                                        <input type="checkbox" id="template-send-sms" onchange="toggleSMSRecipients()"
                                               class="rounded border-gray-300 text-primary-600 focus:ring-primary-500">
                                        <span class="ml-2 text-sm font-medium text-gray-700">${t('alarmTemplates.modal.sendSMS')}</span>
                                    </label>
                                    
                                    <div id="sms-recipients-container" class="hidden mt-3">
                                        <label class="block text-sm font-medium text-gray-700 mb-1">${t('alarmTemplates.modal.smsRecipientsLabel')}</label>
                                        ${usersWithPhone.length === 0 ? `
                                            <div class="rounded-md bg-yellow-50 p-3 mb-2">
                                                <div class="flex">
                                                    <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                                                    </svg>
                                                    <div class="ml-3">
                                                        <p class="text-sm text-yellow-800">${t('alarmTemplates.modal.noPhoneNumbers')}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ` : ''}
                                        ${usersWithoutPhone > 0 && usersWithPhone.length > 0 ? `
                                            <p class="text-xs text-gray-500 mb-2">${t('common.note')}: ${usersWithoutPhone} ${t('alarmTemplates.modal.usersWithoutPhone')}</p>
                                        ` : ''}
                                        <select id="template-sms-recipients" multiple size="5" ${usersWithPhone.length === 0 ? 'disabled' : ''}
                                                class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 ${usersWithPhone.length === 0 ? 'bg-gray-100' : ''}">
                                            ${usersWithPhone.map(user => `
                                                <option value="${user.id}">${user.name} (${user.phone})</option>
                                            `).join('')}
                                        </select>
                                        ${usersWithPhone.length > 0 ? `
                                            <p class="mt-1 text-xs text-gray-500">${t('alarmTemplates.modal.holdCtrl')}</p>
                                        ` : ''}
                                        
                                        <div class="mt-4">
                                            <label class="block text-sm font-medium text-gray-700 mb-1">
                                                ${t('alarmTemplates.modal.smsMessageLabel')}
                                            </label>
                                            <p class="text-xs text-gray-500 mb-2">
                                                ${t('alarmTemplates.modal.smsMessageHelp')}
                                            </p>
                                            <textarea id="template-sms-message" rows="3" ${usersWithPhone.length === 0 ? 'disabled' : ''}
                                                      placeholder="${t('alarmTemplates.modal.smsMessagePlaceholder')}"
                                                      class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 ${usersWithPhone.length === 0 ? 'bg-gray-100' : ''}"></textarea>
                                        </div>
                                        
                                        ${usersWithPhone.length > 0 ? `
                                        <div class="mt-4">
                                            <button type="button" onclick="sendTestSMS()" class="w-full px-4 py-2 text-sm font-medium text-primary-700 bg-primary-50 border border-primary-300 rounded-md hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                                                <svg class="w-4 h-4 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                </svg>
                                                ${t('alarmTemplates.modal.sendTestSMS')}
                                            </button>
                                            <p class="mt-1 text-xs text-gray-500 text-center">${t('alarmTemplates.modal.testSMSHelp')}</p>
                                        </div>
                                        ` : ''}
                                    </div>
                                </div>
                            </div>
                            
                            <div id="temperature-sensor-fields" class="hidden space-y-4">
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-1">${t('alarmTemplates.modal.templateName')}</label>
                                    <input type="text" id="temp-template-name" 
                                           class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                                           placeholder="${t('alarmTemplates.modal.templateNamePlaceholder')}">
                                </div>
                                
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-1">${t('alarmTemplates.modal.description')}</label>
                                    <textarea id="temp-template-description" rows="2"
                                              class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                                              placeholder="${t('alarmTemplates.modal.descriptionPlaceholder')}"></textarea>
                                </div>
                                
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-1">Alarm Type</label>
                                    <select id="temp-alarm-type" required
                                            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 px-3 py-2">
                                        <option value="">Select alarm type</option>
                                        <option value="high_temperature">High Temperature Alarm</option>
                                        <option value="low_temperature">Low Temperature Alarm</option>
                                    </select>
                                    <p class="mt-1 text-xs text-gray-500">Choose whether to alarm on high or low temperature</p>
                                </div>
                                
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-1">Temperature Limit (°C)</label>
                                    <input type="number" id="temp-limit" step="0.1" required
                                           class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                                           placeholder="e.g., 25.0">
                                    <p class="mt-1 text-xs text-gray-500">Temperature threshold that triggers the alarm</p>
                                </div>
                                
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-1">Hysteresis (°C)</label>
                                    <input type="number" id="temp-hysteresis" min="1" step="1" value="1" required
                                           class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500">
                                    <p class="mt-1 text-xs text-gray-500">Temperature must move this many degrees away from limit before a new alarm can trigger (prevents repeated alarms near the threshold)</p>
                                </div>
                                
                                <div class="border-t border-gray-200 pt-4">
                                    <label class="flex items-center">
                                        <input type="checkbox" id="temp-send-sms" onchange="toggleTempSMSRecipients()"
                                               class="rounded border-gray-300 text-primary-600 focus:ring-primary-500">
                                        <span class="ml-2 text-sm font-medium text-gray-700">${t('alarmTemplates.modal.sendSMS')}</span>
                                    </label>
                                    
                                    <div id="temp-sms-recipients-container" class="hidden mt-3">
                                        <label class="block text-sm font-medium text-gray-700 mb-1">${t('alarmTemplates.modal.smsRecipientsLabel')}</label>
                                        ${usersWithPhone.length === 0 ? `
                                            <div class="rounded-md bg-yellow-50 p-3 mb-2">
                                                <div class="flex">
                                                    <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                                                    </svg>
                                                    <div class="ml-3">
                                                        <p class="text-sm text-yellow-800">${t('alarmTemplates.modal.noPhoneNumbers')}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ` : ''}
                                        <select id="temp-sms-recipients" multiple size="5" ${usersWithPhone.length === 0 ? 'disabled' : ''}
                                                class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 ${usersWithPhone.length === 0 ? 'bg-gray-100' : ''}">
                                            ${usersWithPhone.map(user => `
                                                <option value="${user.id}">${user.name} (${user.phone})</option>
                                            `).join('')}
                                        </select>
                                        ${usersWithPhone.length > 0 ? `
                                            <p class="mt-1 text-xs text-gray-500">${t('alarmTemplates.modal.holdCtrl')}</p>
                                        ` : ''}
                                        
                                        <div class="mt-4">
                                            <label class="block text-sm font-medium text-gray-700 mb-1">
                                                ${t('alarmTemplates.modal.smsMessageLabel')}
                                            </label>
                                            <p class="text-xs text-gray-500 mb-2">
                                                ${t('alarmTemplates.modal.smsMessageHelp')}
                                            </p>
                                            <textarea id="temp-sms-message" rows="3" ${usersWithPhone.length === 0 ? 'disabled' : ''}
                                                      placeholder="${t('alarmTemplates.modal.smsMessagePlaceholder')}"
                                                      class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 ${usersWithPhone.length === 0 ? 'bg-gray-100' : ''}"></textarea>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div id="missing-checkin-fields" class="hidden space-y-4">
                                <div class="rounded-md bg-blue-50 p-3">
                                    <p class="text-sm text-blue-800">${t('alarmTemplates.modal.missingCheckinIntro')}</p>
                                </div>
                                
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-1">${t('alarmTemplates.modal.templateName')}</label>
                                    <input type="text" id="mc-template-name"
                                           class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                                           placeholder="${t('alarmTemplates.modal.templateNamePlaceholder')}">
                                </div>
                                
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-1">${t('alarmTemplates.modal.description')}</label>
                                    <textarea id="mc-template-description" rows="2"
                                              class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                                              placeholder="${t('alarmTemplates.modal.descriptionPlaceholder')}"></textarea>
                                </div>
                                
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-1">${t('alarmTemplates.modal.timezone')}</label>
                                    <input type="text" id="mc-template-timezone" readonly
                                           class="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm"
                                           value="">
                                    <p class="mt-1 text-xs text-gray-500">${t('alarmTemplates.modal.timezoneHelp')}</p>
                                </div>
                                
                                <div class="grid grid-cols-2 gap-4">
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 mb-1">${t('alarmTemplates.modal.reportTime')}</label>
                                        <input type="time" id="mc-check-time" value="00:00"
                                               class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500">
                                        <p class="mt-1 text-xs text-gray-500">${t('alarmTemplates.modal.reportTimeHelp')}</p>
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 mb-1">${t('alarmTemplates.modal.lookbackHours')}</label>
                                        <input type="number" id="mc-lookback-hours" min="1" max="168" value="24"
                                               class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500">
                                        <p class="mt-1 text-xs text-gray-500">${t('alarmTemplates.modal.lookbackHoursHelp')}</p>
                                    </div>
                                </div>
                                
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-2">${t('alarmTemplates.modal.weekdays')}</label>
                                    <div class="flex flex-wrap gap-3">
                                        ${['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => `
                                            <label class="flex items-center text-sm">
                                                <input type="checkbox" name="mc-weekday-${day}" checked
                                                       class="rounded border-gray-300 text-primary-600 focus:ring-primary-500">
                                                <span class="ml-1">${t('weekdays.' + day)}</span>
                                            </label>
                                        `).join('')}
                                    </div>
                                </div>
                                
                                <div class="border-t border-gray-200 pt-4">
                                    <label class="block text-sm font-medium text-gray-700 mb-1">${t('alarmTemplates.modal.emailRecipientsLabel')}</label>
                                    ${emailUsers.length === 0 ? `
                                        <div class="rounded-md bg-yellow-50 p-3 mb-2">
                                            <p class="text-sm text-yellow-800">${t('alarmTemplates.modal.noEmailRecipients')}</p>
                                        </div>
                                    ` : ''}
                                    <select id="mc-email-recipients" multiple size="5" ${emailUsers.length === 0 ? 'disabled' : ''}
                                            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 ${emailUsers.length === 0 ? 'bg-gray-100' : ''}">
                                        ${emailUsers.map(user => `
                                            <option value="${user.id}">${user.name} (${user.email})</option>
                                        `).join('')}
                                    </select>
                                    <p class="mt-1 text-xs text-gray-500">${t('alarmTemplates.modal.emailRecipientsHelp')}</p>
                                    
                                    <div class="mt-4">
                                        <label class="block text-sm font-medium text-gray-700 mb-1">${t('alarmTemplates.modal.emailMessageLabel')}</label>
                                        <textarea id="mc-email-message" rows="3"
                                                  placeholder="${t('alarmTemplates.modal.emailMessagePlaceholder')}"
                                                  class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"></textarea>
                                    </div>
                                </div>
                            </div>
                            
                            <div id="other-sensor-fields" class="hidden">
                                <div class="rounded-md bg-yellow-50 p-4">
                                    <div class="flex">
                                        <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                                        </svg>
                                        <div class="ml-3">
                                            <h3 class="text-sm font-medium text-yellow-800">${t('alarmTemplates.modal.comingSoon')}</h3>
                                            <p class="mt-2 text-sm text-yellow-700">${t('alarmTemplates.modal.comingSoonDesc')}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
                <div class="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                    <button type="button" onclick="window.closeModal()" class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                        ${t('alarmTemplates.modal.cancel')}
                    </button>
                    <button type="submit" id="create-template-submit-btn" form="alarm-template-form" disabled class="px-4 py-2 text-sm font-medium text-white bg-gray-400 rounded-md cursor-not-allowed">
                        ${isEditMode ? t('alarmTemplates.modal.update') : t('alarmTemplates.modal.create')}
                    </button>
                </div>
            </div>
        </div>
    `;
    
    if (modal) {
        modal.innerHTML = modalHtml;
        modal.classList.remove('hidden');
        
        // Add form submit handler
        const form = document.getElementById('alarm-template-form');
        if (form) {
            form.addEventListener('submit', isEditMode ? updateAlarmTemplate : createAlarmTemplate);
        }
        
        // Set timezone from browser or existing template
        const timezoneValue = existingTemplate?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
        ['template-timezone', 'mc-template-timezone'].forEach(id => {
            const input = document.getElementById(id);
            if (input) input.value = timezoneValue;
        });
        
        // Show the appropriate fields based on sensor type (important for edit mode)
        toggleAlarmFields();
        
        if (isEditMode) {
            // Populate fields with existing template data
            const sensorType = document.getElementById('template-sensor-type')?.value;
            
            if (sensorType === 'missing_checkin') {
                const config = existingTemplate.missing_checkin_config || {};
                document.getElementById('mc-template-name').value = existingTemplate.template_name || '';
                document.getElementById('mc-template-description').value = existingTemplate.description || '';
                document.getElementById('mc-check-time').value = config.check_time || '00:00';
                document.getElementById('mc-lookback-hours').value = config.lookback_hours || 24;

                ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].forEach(day => {
                    const checkbox = document.querySelector(`input[name="mc-weekday-${day}"]`);
                    if (checkbox) checkbox.checked = config.weekdays ? !!config.weekdays[day] : true;
                });

                const mcRecipientsSelect = document.getElementById('mc-email-recipients');
                const selectedIds = existingTemplate.actions?.email_recipient_user_ids || [];
                if (mcRecipientsSelect) {
                    Array.from(mcRecipientsSelect.options).forEach(option => {
                        option.selected = selectedIds.includes(option.value);
                    });
                }

                document.getElementById('mc-email-message').value = existingTemplate.actions?.email_message || '';
            } else if (sensorType === 'temperature_humidity' && existingTemplate.temperature_config) {
                // Temperature alarm fields
                document.getElementById('temp-template-name').value = existingTemplate.template_name || '';
                document.getElementById('temp-template-description').value = existingTemplate.description || '';
                document.getElementById('temp-alarm-type').value = existingTemplate.temperature_config.alarm_type || 'high_temperature';
                document.getElementById('temp-limit').value = existingTemplate.temperature_config.temperature_limit || '';
                document.getElementById('temp-hysteresis').value = existingTemplate.temperature_config.hysteresis || 1;
                
                // Populate SMS settings for temperature alarms
                if (existingTemplate.actions?.send_sms) {
                    document.getElementById('temp-send-sms').checked = true;
                    toggleTempSMSRecipients();
                    
                    // Select recipients
                    const recipientsSelect = document.getElementById('temp-sms-recipients');
                    if (recipientsSelect && existingTemplate.actions.sms_recipient_user_ids) {
                        Array.from(recipientsSelect.options).forEach(option => {
                            if (existingTemplate.actions.sms_recipient_user_ids.includes(option.value)) {
                                option.selected = true;
                            }
                        });
                    }
                    
                    // Set SMS message
                    if (existingTemplate.actions.sms_message) {
                        document.getElementById('temp-sms-message').value = existingTemplate.actions.sms_message;
                    }
                }
            } else {
                // Card reader/registration alarm fields
                document.getElementById('template-name').value = existingTemplate.template_name || '';
                document.getElementById('template-description').value = existingTemplate.description || '';
            
                // Populate SMS settings
                if (existingTemplate.actions?.send_sms) {
                    document.getElementById('template-send-sms').checked = true;
                    toggleSMSRecipients();
                    
                    // Select recipients
                    const recipientsSelect = document.getElementById('template-sms-recipients');
                    if (recipientsSelect && existingTemplate.actions.sms_recipient_user_ids) {
                        Array.from(recipientsSelect.options).forEach(option => {
                            if (existingTemplate.actions.sms_recipient_user_ids.includes(option.value)) {
                                option.selected = true;
                            }
                        });
                    }
                    
                    // Set SMS message
                    if (existingTemplate.actions.sms_message) {
                        document.getElementById('template-sms-message').value = existingTemplate.actions.sms_message;
                    }
                }
            
                // Add schedules
                if (existingTemplate.schedules && existingTemplate.schedules.length > 0) {
                    existingTemplate.schedules.forEach((schedule, index) => {
                        addSchedule(schedule);
                    });
                } else {
                    addSchedule();
                }
            }
        } else {
            // Initialize with one empty schedule for create mode
            addSchedule();
        }
    }
}

/**
 * Add schedule to form
 */
export function addSchedule(scheduleData = null) {
    const container = document.getElementById('schedules-container');
    const addButton = document.getElementById('add-schedule-btn');
    
    if (!container) return;
    
    // Count existing schedules
    const existingSchedules = container.querySelectorAll('.schedule-item').length;
    
    if (existingSchedules >= 7) {
        showError('Maximum 7 schedules allowed per template');
        return;
    }
    
    scheduleCounter++;
    const scheduleId = `schedule-${scheduleCounter}`;
    
    const checkTime = scheduleData?.check_time || '';
    const maxTime = scheduleData?.max_time_since_registration || 30;
    const weekdays = scheduleData?.weekdays || {};
    
    const scheduleHtml = `
        <div class="schedule-item border border-gray-200 rounded-lg p-3 mb-3 bg-white" data-schedule-id="${scheduleId}">
            <div class="flex items-start justify-between mb-2">
                <span class="text-sm font-medium text-gray-700">${t('alarmTemplates.schedule')} ${existingSchedules + 1}</span>
                ${existingSchedules > 0 ? `
                    <button type="button" onclick="removeSchedule('${scheduleId}')" 
                            class="text-red-600 hover:text-red-700">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                    </button>
                ` : ''}
            </div>
            
            <div class="grid grid-cols-2 gap-3 mb-3">
                <div>
                    <label class="block text-xs text-gray-600 mb-1">${t('alarmTemplates.modal.checkTime')}</label>
                    <input type="time" name="check-time" required value="${checkTime}"
                           class="block w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500">
                </div>
                
                <div>
                    <label class="block text-xs text-gray-600 mb-1">${t('alarmTemplates.modal.maxTime')}</label>
                    <input type="number" name="max-time" min="1" value="${maxTime}" required
                           class="block w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500">
                </div>
            </div>
            
            <div>
                <label class="block text-xs text-gray-600 mb-1">${t('alarmTemplates.modal.activeDays')}</label>
                <div class="grid grid-cols-7 gap-1">
                    ${['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day, index) => {
                        const isChecked = scheduleData?.weekdays?.[day] ?? (index < 5); // Default Mon-Fri checked
                        const dayAbbr = {
                            'monday': t('weekdays.mon'),
                            'tuesday': t('weekdays.tue'),
                            'wednesday': t('weekdays.wed'),
                            'thursday': t('weekdays.thu'),
                            'friday': t('weekdays.fri'),
                            'saturday': t('weekdays.sat'),
                            'sunday': t('weekdays.sun')
                        }[day];
                        return `
                        <label class="flex flex-col items-center text-center">
                            <input type="checkbox" name="weekday-${day}" ${isChecked ? 'checked' : ''}
                                   class="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mb-1">
                            <span class="text-xs text-gray-600">${dayAbbr}</span>
                        </label>
                    `}).join('')}
                </div>
            </div>
        </div>
    `;
    
    container.insertAdjacentHTML('beforeend', scheduleHtml);
    
    // Update add button state
    if (existingSchedules + 1 >= 7) {
        addButton.disabled = true;
        addButton.classList.add('opacity-50', 'cursor-not-allowed');
    }
    
    // Renumber schedules
    renumberSchedules();
}

/**
 * Remove schedule from form
 */
export function removeSchedule(scheduleId) {
    const container = document.getElementById('schedules-container');
    const addButton = document.getElementById('add-schedule-btn');
    const schedule = container?.querySelector(`[data-schedule-id="${scheduleId}"]`);
    
    if (schedule) {
        schedule.remove();
        
        // Re-enable add button if we're below limit
        const remainingSchedules = container.querySelectorAll('.schedule-item').length;
        if (remainingSchedules < 7) {
            addButton.disabled = false;
            addButton.classList.remove('opacity-50', 'cursor-not-allowed');
        }
        
        // Renumber remaining schedules
        renumberSchedules();
    }
}

/**
 * Renumber schedules
 */
function renumberSchedules() {
    const container = document.getElementById('schedules-container');
    if (!container) return;
    
    const schedules = container.querySelectorAll('.schedule-item');
    schedules.forEach((schedule, index) => {
        const label = schedule.querySelector('span');
        if (label) {
            label.textContent = `${t('alarmTemplates.schedule')} ${index + 1}`;
        }
    });
}

/**
 * Toggle alarm fields based on sensor type
 */
export function toggleAlarmFields() {
    const sensorType = document.getElementById('template-sensor-type')?.value;
    const comingSoonMessage = document.getElementById('coming-soon-message');
    const cardReaderFields = document.getElementById('card-reader-fields');
    const temperatureFields = document.getElementById('temperature-sensor-fields');
    const missingCheckinFields = document.getElementById('missing-checkin-fields');
    const otherSensorFields = document.getElementById('other-sensor-fields');
    const submitBtn = document.getElementById('create-template-submit-btn');
    const templateNameInput = document.getElementById('template-name');
    const tempTemplateNameInput = document.getElementById('temp-template-name');
    const tempRequiredInputs = [
        document.getElementById('temp-alarm-type'),
        document.getElementById('temp-limit'),
        document.getElementById('temp-hysteresis')
    ];
    
    missingCheckinFields?.classList.add('hidden');
    // Hidden required fields block form submission ("not focusable" error), so
    // only keep them required while the temperature fields are actually shown
    tempRequiredInputs.forEach(input => {
        if (input) input.required = (sensorType === 'temperature_humidity');
    });
    
    if (!sensorType) {
        // No sensor type selected
        comingSoonMessage?.classList.remove('hidden');
        cardReaderFields?.classList.add('hidden');
        temperatureFields?.classList.add('hidden');
        otherSensorFields?.classList.add('hidden');
        submitBtn?.setAttribute('disabled', 'true');
        submitBtn?.classList.add('bg-gray-400', 'cursor-not-allowed');
        submitBtn?.classList.remove('bg-primary-600', 'hover:bg-primary-700');
    } else if (sensorType === 'card_reader') {
        // Card reader - show full fields
        comingSoonMessage?.classList.add('hidden');
        cardReaderFields?.classList.remove('hidden');
        temperatureFields?.classList.add('hidden');
        otherSensorFields?.classList.add('hidden');
        submitBtn?.removeAttribute('disabled');
        submitBtn?.classList.remove('bg-gray-400', 'cursor-not-allowed');
        submitBtn?.classList.add('bg-primary-600', 'hover:bg-primary-700');
        
        // Make template name required
        if (templateNameInput) templateNameInput.required = true;
        if (tempTemplateNameInput) tempTemplateNameInput.required = false;
        
        // Enable required on schedule fields
        cardReaderFields?.querySelectorAll('input[name="check-time"]').forEach(input => {
            input.required = true;
        });
        cardReaderFields?.querySelectorAll('input[name="max-time"]').forEach(input => {
            input.required = true;
        });
    } else if (sensorType === 'temperature_humidity') {
        // Temperature/Humidity sensor - show temperature fields
        comingSoonMessage?.classList.add('hidden');
        cardReaderFields?.classList.add('hidden');
        temperatureFields?.classList.remove('hidden');
        otherSensorFields?.classList.add('hidden');
        submitBtn?.removeAttribute('disabled');
        submitBtn?.classList.remove('bg-gray-400', 'cursor-not-allowed');
        submitBtn?.classList.add('bg-primary-600', 'hover:bg-primary-700');
        
        // Make temperature template name required
        if (tempTemplateNameInput) tempTemplateNameInput.required = true;
        if (templateNameInput) templateNameInput.required = false;
        
        // Disable required on schedule fields (they're hidden)
        cardReaderFields?.querySelectorAll('input[name="check-time"]').forEach(input => {
            input.required = false;
        });
        cardReaderFields?.querySelectorAll('input[name="max-time"]').forEach(input => {
            input.required = false;
        });
    } else if (sensorType === 'missing_checkin') {
        // Account-level missing check-in report
        comingSoonMessage?.classList.add('hidden');
        cardReaderFields?.classList.add('hidden');
        temperatureFields?.classList.add('hidden');
        otherSensorFields?.classList.add('hidden');
        missingCheckinFields?.classList.remove('hidden');
        submitBtn?.removeAttribute('disabled');
        submitBtn?.classList.remove('bg-gray-400', 'cursor-not-allowed');
        submitBtn?.classList.add('bg-primary-600', 'hover:bg-primary-700');
        
        if (templateNameInput) templateNameInput.required = false;
        if (tempTemplateNameInput) tempTemplateNameInput.required = false;
        
        cardReaderFields?.querySelectorAll('input[name="check-time"], input[name="max-time"]').forEach(input => {
            input.required = false;
        });
    } else {
        // Other sensor types - show coming soon
        comingSoonMessage?.classList.add('hidden');
        cardReaderFields?.classList.add('hidden');
        temperatureFields?.classList.add('hidden');
        otherSensorFields?.classList.remove('hidden');
        submitBtn?.setAttribute('disabled', 'true');
        submitBtn?.classList.add('bg-gray-400', 'cursor-not-allowed');
        submitBtn?.classList.remove('bg-primary-600', 'hover:bg-primary-700');
    }
}

/**
 * Toggle SMS recipients section for temperature alarms
 */
export function toggleTempSMSRecipients() {
    const checkbox = document.getElementById('temp-send-sms');
    const container = document.getElementById('temp-sms-recipients-container');
    if (checkbox && container) {
        container.classList.toggle('hidden', !checkbox.checked);
    }
}

/**
 * Toggle SMS recipients section
 */
export function toggleSMSRecipients() {
    const checkbox = document.getElementById('template-send-sms');
    const container = document.getElementById('sms-recipients-container');
    if (checkbox && container) {
        container.classList.toggle('hidden', !checkbox.checked);
    }
}

/**
 * Send test SMS
 */
export async function sendTestSMS() {
    // Implementation continues in next message due to length...
    try {
        const select = document.getElementById('template-sms-recipients');
        const messageInput = document.getElementById('template-sms-message');
        
        if (!select || select.selectedOptions.length === 0) {
            showError('Please select at least one recipient to send test SMS');
            return;
        }
        
        const userIds = Array.from(select.selectedOptions).map(opt => opt.value);
        const customMessage = messageInput?.value.trim() || null;
        
        const response = await fetch(`${API_BASE_URL}/alarm-templates/test-sms`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${auth.getToken()}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                user_ids: userIds,
                custom_message: customMessage
            })
        });
        
        const result = await response.json();
        
        if (response.ok && result.success) {
            await showAlertDialog({
                title: 'Test SMS Sent',
                message: result.message || 'Test SMS sent successfully!',
                icon: 'success'
            });
        } else {
            await showAlertDialog({
                title: 'Failed to Send Test SMS',
                message: result.message || 'Failed to send test SMS',
                icon: 'error'
            });
        }
    } catch (error) {
        console.error('Error sending test SMS:', error);
        await showAlertDialog({
            title: 'Error',
            message: 'An error occurred while sending test SMS: ' + error.message,
            icon: 'error'
        });
    }
}

/**
 * Load users for SMS selection
 */
async function loadUsersForSMS() {
    try {
        const response = await fetch(`${API_BASE_URL}/users/for-sms`, {
            headers: { 'Authorization': `Bearer ${auth.getToken()}` }
        });
        
        if (!response.ok) {
            console.error('Failed to load users:', response.status);
            return [];
        }
        
        const data = await response.json();
        return data.users || [];
    } catch (error) {
        console.error('Error loading users:', error);
        return [];
    }
}

/**
 * Load users for email recipient selection
 */
async function loadUsersForEmail() {
    try {
        const response = await fetch(`${API_BASE_URL}/users/for-email`, {
            headers: { 'Authorization': `Bearer ${auth.getToken()}` }
        });
        
        if (!response.ok) {
            console.error('Failed to load email recipients:', response.status);
            return [];
        }
        
        const data = await response.json();
        return data.users || [];
    } catch (error) {
        console.error('Error loading email recipients:', error);
        return [];
    }
}

/**
 * Create alarm template
 */
export async function createAlarmTemplate(event) {
    event.preventDefault();
    
    try {
        const sensorType = document.getElementById('template-sensor-type');
        
        // Determine if this is a temperature alarm or card reader alarm
        if (sensorType.value === 'temperature_humidity') {
            return await createTemperatureAlarmTemplate();
        } else if (sensorType.value === 'card_reader') {
            return await createCardReaderAlarmTemplate();
        } else if (sensorType.value === 'missing_checkin') {
            return await saveMissingCheckinTemplate(null);
        } else {
            showError('Invalid sensor type selected');
            return;
        }
    } catch (error) {
        console.error('Error creating alarm template:', error);
        showError(error.message);
    }
}

/**
 * Create or update an account-level missing check-in template
 */
async function saveMissingCheckinTemplate(templateId) {
    const templateName = document.getElementById('mc-template-name').value.trim();
    const description = document.getElementById('mc-template-description').value.trim();
    const timezone = document.getElementById('mc-template-timezone').value;
    const checkTime = document.getElementById('mc-check-time').value;
    const lookbackHours = parseInt(document.getElementById('mc-lookback-hours').value, 10);
    const emailMessage = document.getElementById('mc-email-message').value.trim();
    const recipientsSelect = document.getElementById('mc-email-recipients');
    const recipientIds = recipientsSelect ? Array.from(recipientsSelect.selectedOptions).map(opt => opt.value) : [];
    
    if (!templateName) {
        showError(t('alarmTemplates.modal.templateNameRequired'));
        return;
    }
    
    if (!checkTime) {
        showError(t('alarmTemplates.modal.reportTimeRequired'));
        return;
    }
    
    if (!lookbackHours || lookbackHours < 1 || lookbackHours > 168) {
        showError(t('alarmTemplates.modal.lookbackHoursInvalid'));
        return;
    }
    
    if (recipientIds.length === 0) {
        showError(t('alarmTemplates.modal.emailRecipientsRequired'));
        return;
    }
    
    const weekdays = {};
    ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].forEach(day => {
        weekdays[day] = document.querySelector(`input[name="mc-weekday-${day}"]`)?.checked || false;
    });
    
    const payload = {
        template_name: templateName,
        description: description || null,
        sensor_type: 'card_reader',
        alarm_type: 'missing_checkin_report',
        timezone: timezone,
        missing_checkin_config: {
            check_time: checkTime,
            lookback_hours: lookbackHours,
            weekdays: weekdays
        },
        actions: {
            create_alarm_event: true,
            send_sms: false,
            send_email: true,
            email_recipient_user_ids: recipientIds,
            email_message: emailMessage || null
        }
    };
    
    const response = await fetch(
        templateId
            ? `${API_BASE_URL}/alarm-templates/templates/${templateId}`
            : `${API_BASE_URL}/alarm-templates/templates`,
        {
            method: templateId ? 'PATCH' : 'POST',
            headers: {
                'Authorization': `Bearer ${auth.getToken()}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        }
    );
    
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save alarm template');
    }
    
    showSuccess(templateId ? 'Alarm template updated successfully' : 'Alarm template created successfully');
    closeModal();
    
    if (window.loadAlarmTemplates) {
        await window.loadAlarmTemplates();
    }
}

/**
 * Create temperature alarm template
 */
async function createTemperatureAlarmTemplate() {
    const templateName = document.getElementById('temp-template-name');
    const description = document.getElementById('temp-template-description');
    const alarmType = document.getElementById('temp-alarm-type');
    const tempLimit = document.getElementById('temp-limit');
    const hysteresis = document.getElementById('temp-hysteresis');
    const sendSMS = document.getElementById('temp-send-sms').checked;
    
    // Validate
    if (!templateName.value.trim()) {
        showError('Template name is required');
        return;
    }
    
    if (!alarmType.value) {
        showError('Please select an alarm type (High or Low Temperature)');
        return;
    }
    
    if (!tempLimit.value || isNaN(parseFloat(tempLimit.value))) {
        showError('Please enter a valid temperature limit');
        return;
    }
    
    if (!hysteresis.value || isNaN(parseInt(hysteresis.value)) || parseInt(hysteresis.value) < 1) {
        showError('Hysteresis must be at least 1 degree');
        return;
    }
    
    // Build actions object
    const actions = {
        create_alarm_event: true,
        send_sms: sendSMS,
        sms_recipient_user_ids: [],
        sms_message: null
    };
    
    if (sendSMS) {
        const select = document.getElementById('temp-sms-recipients');
        if (select) {
            actions.sms_recipient_user_ids = Array.from(select.selectedOptions).map(opt => opt.value);
        }
        
        const smsMessage = document.getElementById('temp-sms-message');
        if (smsMessage && smsMessage.value.trim()) {
            actions.sms_message = smsMessage.value.trim();
        }
    }
    
    const response = await fetch(`${API_BASE_URL}/alarm-templates/templates`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${auth.getToken()}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            template_name: templateName.value,
            description: description.value || null,
            sensor_type: 'temperature_humidity',
            alarm_type: alarmType.value,
            temperature_config: {
                alarm_type: alarmType.value,
                temperature_limit: parseFloat(tempLimit.value),
                hysteresis: parseInt(hysteresis.value)
            },
            actions: actions
        })
    });
    
    if (!response.ok) {
        const error = await response.json();
        console.error('Temperature alarm creation error:', error);
        
        // Handle validation errors (422)
        let errorMessage = 'Failed to create temperature alarm template';
        if (error.detail) {
            if (Array.isArray(error.detail)) {
                errorMessage = error.detail.map(e => `${e.loc?.join('.')}: ${e.msg}`).join(', ');
            } else if (typeof error.detail === 'string') {
                errorMessage = error.detail;
            } else {
                errorMessage = JSON.stringify(error.detail);
            }
        }
        throw new Error(errorMessage);
    }
    
    showSuccess('Temperature alarm template created successfully');
    closeModal();
    
    // Reload alarm templates list
    if (window.loadAlarmTemplates) {
        await window.loadAlarmTemplates();
    }
}

/**
 * Create card reader alarm template
 */
async function createCardReaderAlarmTemplate() {
    const templateName = document.getElementById('template-name');
    const description = document.getElementById('template-description');
    const sendSMS = document.getElementById('template-send-sms').checked;
    const timezone = document.getElementById('template-timezone');
    
    // Validate
    if (!templateName.value.trim()) {
        showError('Template name is required');
        return;
    }
    
    // Collect schedules
    const schedulesContainer = document.getElementById('schedules-container');
    const scheduleElements = schedulesContainer?.querySelectorAll('.schedule-item');
    
    if (!scheduleElements || scheduleElements.length === 0) {
        showError('At least one schedule is required');
        return;
    }
    
    const schedules = [];
    scheduleElements?.forEach((scheduleEl) => {
        const checkTime = scheduleEl.querySelector('input[name="check-time"]').value;
        const maxTime = parseInt(scheduleEl.querySelector('input[name="max-time"]').value);
        
        const weekdays = {};
        ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].forEach(day => {
            const checkbox = scheduleEl.querySelector(`input[name="weekday-${day}"]`);
            weekdays[day] = checkbox ? checkbox.checked : false;
        });
        
        schedules.push({
            check_time: checkTime,
            max_time_since_registration: maxTime,
            weekdays: weekdays
        });
    });
    
    // Build actions object
    const actions = {
        create_alarm_event: true,
        send_sms: sendSMS,
        sms_recipient_user_ids: [],
        sms_message: null
    };
    
    if (sendSMS) {
        const select = document.getElementById('template-sms-recipients');
        if (select) {
            actions.sms_recipient_user_ids = Array.from(select.selectedOptions).map(opt => opt.value);
        }
        
        const smsMessage = document.getElementById('template-sms-message');
        if (smsMessage && smsMessage.value.trim()) {
            actions.sms_message = smsMessage.value.trim();
        }
    }
    
    const response = await fetch(`${API_BASE_URL}/alarm-templates/templates`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${auth.getToken()}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            template_name: templateName.value,
            description: description.value || null,
            sensor_type: 'card_reader',
            alarm_type: 'registration',
            timezone: timezone.value,
            schedules: schedules,
            actions: actions
        })
    });
    
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create alarm template');
    }
    
    showSuccess('Alarm template created successfully');
    closeModal();
    
    // Reload alarm templates list
    if (window.loadAlarmTemplates) {
        await window.loadAlarmTemplates();
    }
}

/**
 * Update alarm template
 */
export async function updateAlarmTemplate(event) {
    event.preventDefault();
    
    try {
        const templateId = document.getElementById('template-id').value;
        const sensorType = document.getElementById('template-sensor-type')?.value;
        
        if (sensorType === 'temperature_humidity') {
            await updateTemperatureAlarmTemplate(templateId);
        } else if (sensorType === 'missing_checkin') {
            await saveMissingCheckinTemplate(templateId);
        } else {
            await updateCardReaderAlarmTemplate(templateId);
        }
        
    } catch (error) {
        console.error('Error updating alarm template:', error);
        showError(error.message);
    }
}

async function updateTemperatureAlarmTemplate(templateId) {
    const templateName = document.getElementById('temp-template-name');
    const description = document.getElementById('temp-template-description');
    const alarmType = document.getElementById('temp-alarm-type');
    const tempLimit = document.getElementById('temp-limit');
    const hysteresis = document.getElementById('temp-hysteresis');
    const sendSMS = document.getElementById('temp-send-sms')?.checked || false;
    
    // Build actions object
    const actions = {
        create_alarm_event: true,
        send_sms: sendSMS,
        sms_recipient_user_ids: [],
        sms_message: null
    };
    
    if (sendSMS) {
        const select = document.getElementById('temp-sms-recipients');
        if (select) {
            actions.sms_recipient_user_ids = Array.from(select.selectedOptions).map(opt => opt.value);
        }
        
        const smsMessage = document.getElementById('temp-sms-message');
        if (smsMessage && smsMessage.value.trim()) {
            actions.sms_message = smsMessage.value.trim();
        }
    }
    
    const response = await fetch(`${API_BASE_URL}/alarm-templates/templates/${templateId}`, {
        method: 'PATCH',
        headers: {
            'Authorization': `Bearer ${auth.getToken()}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            template_name: templateName.value,
            description: description.value || null,
            sensor_type: 'temperature_humidity',
            alarm_type: alarmType.value,
            temperature_config: {
                alarm_type: alarmType.value,
                temperature_limit: parseFloat(tempLimit.value),
                hysteresis: parseInt(hysteresis.value)
            },
            actions: actions
        })
    });
    
    if (!response.ok) {
        const error = await response.json();
        console.error('Temperature alarm update error:', error);
        
        let errorMessage = 'Failed to update temperature alarm template';
        if (error.detail) {
            if (Array.isArray(error.detail)) {
                errorMessage = error.detail.map(e => `${e.loc?.join('.')}: ${e.msg}`).join(', ');
            } else if (typeof error.detail === 'string') {
                errorMessage = error.detail;
            } else {
                errorMessage = JSON.stringify(error.detail);
            }
        }
        throw new Error(errorMessage);
    }
    
    showSuccess('Temperature alarm template updated successfully');
    closeModal();
    
    // Reload alarm templates list
    if (window.loadAlarmTemplates) {
        await window.loadAlarmTemplates();
    }
}

async function updateCardReaderAlarmTemplate(templateId) {
    const templateName = document.getElementById('template-name');
    const description = document.getElementById('template-description');
    const sendSMS = document.getElementById('template-send-sms').checked;
    const timezone = document.getElementById('template-timezone');
        
    // Collect schedules (same as create)
    const schedulesContainer = document.getElementById('schedules-container');
    const scheduleElements = schedulesContainer?.querySelectorAll('.schedule-item');
    
    const schedules = [];
    scheduleElements?.forEach((scheduleEl) => {
        const checkTime = scheduleEl.querySelector('input[name="check-time"]').value;
        const maxTime = parseInt(scheduleEl.querySelector('input[name="max-time"]').value);
        
        const weekdays = {};
        ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].forEach(day => {
            const checkbox = scheduleEl.querySelector(`input[name="weekday-${day}"]`);
            weekdays[day] = checkbox ? checkbox.checked : false;
        });
        
        schedules.push({
            check_time: checkTime,
            max_time_since_registration: maxTime,
            weekdays: weekdays
        });
    });
    
    // Build actions object
    const actions = {
        create_alarm_event: true,
        send_sms: sendSMS,
        sms_recipient_user_ids: [],
        sms_message: null
    };
    
    if (sendSMS) {
        const select = document.getElementById('template-sms-recipients');
        if (select) {
            actions.sms_recipient_user_ids = Array.from(select.selectedOptions).map(opt => opt.value);
        }
        
        const smsMessage = document.getElementById('template-sms-message');
        if (smsMessage && smsMessage.value.trim()) {
            actions.sms_message = smsMessage.value.trim();
        }
    }
    
    const response = await fetch(`${API_BASE_URL}/alarm-templates/templates/${templateId}`, {
        method: 'PATCH',
        headers: {
            'Authorization': `Bearer ${auth.getToken()}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            template_name: templateName.value,
            description: description.value || null,
            timezone: timezone.value,
            schedules: schedules,
            actions: actions
        })
    });
    
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update alarm template');
    }
    
    showSuccess('Alarm template updated successfully');
    closeModal();
    
    // Reload alarm templates list
    if (window.loadAlarmTemplates) {
        await window.loadAlarmTemplates();
    }
}

/**
 * Show edit alarm template modal
 */
export async function showEditAlarmTemplateModal(templateId) {
    try {
        // Show loading state
        const modal = document.getElementById('modal');
        if (modal) {
            modal.innerHTML = `
                <div class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
                    <div class="bg-white rounded-lg shadow-xl p-8">
                        <div class="text-center">
                            <svg class="animate-spin h-8 w-8 text-primary-600 mx-auto mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <p class="text-sm text-gray-600">Loading template...</p>
                        </div>
                    </div>
                </div>
            `;
            modal.classList.remove('hidden');
        }
        
        // Fetch template data
        const response = await fetch(`${API_BASE_URL}/alarm-templates/templates/${templateId}`, {
            headers: { 'Authorization': `Bearer ${auth.getToken()}` }
        });
        
        if (!response.ok) {
            throw new Error('Failed to load template');
        }
        
        const template = await response.json();
        
        // Call showCreateAlarmTemplateModal but with edit mode
        await showCreateAlarmTemplateModal(template);
        
    } catch (error) {
        console.error('Error loading template for edit:', error);
        closeModal();
        await showAlertDialog({
            title: 'Error',
            message: 'Failed to load template for editing: ' + error.message,
            icon: 'error'
        });
    }
}

/**
 * Delete alarm template
 */
export async function deleteAlarmTemplate(templateId) {
    const confirmed = await showConfirmDialog({
        title: 'Delete Alarm Template',
        message: 'Are you sure you want to delete this alarm template? If any sensors are using it, they will be unassigned automatically. This action cannot be undone.',
        confirmText: 'Delete',
        cancelText: 'Cancel'
    });
    
    if (!confirmed) {
        return;
    }
    
    try {
        const response = await fetch(`${API_BASE_URL}/alarm-templates/templates/${templateId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${auth.getToken()}` }
        });
        
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to delete alarm template');
        }
        
        showSuccess('Alarm template deleted successfully');
        
        // Reload alarm templates list
        if (window.loadAlarmTemplates) {
            await window.loadAlarmTemplates();
        }
        
    } catch (error) {
        console.error('Error deleting alarm template:', error);
        showError(error.message);
    }
}

// ============================================================================
// Account-level alarm assignments
// ============================================================================

function flattenAccountTree(accounts, level = 0, output = []) {
    accounts.forEach(account => {
        output.push({
            id: account._id || account.id,
            name: account.account_name || account.name || 'Unnamed Account',
            level
        });
        if (account.children && account.children.length > 0) {
            flattenAccountTree(account.children, level + 1, output);
        }
    });
    return output;
}

/**
 * Show the modal for assigning an account-level alarm to accounts
 */
export async function showAccountAlarmAssignments(templateId) {
    const modal = document.getElementById('modal');
    if (!modal) return;

    try {
        const headers = { 'Authorization': `Bearer ${auth.getToken()}` };

        const [hierarchyResponse, assignmentsResponse] = await Promise.all([
            fetch(`${API_BASE_URL}/accounts/hierarchy`, { headers }),
            fetch(`${API_BASE_URL}/alarm-templates/account-assignments?template_id=${templateId}`, { headers })
        ]);

        if (!hierarchyResponse.ok) throw new Error('Failed to load accounts');
        if (!assignmentsResponse.ok) throw new Error('Failed to load assignments');

        const hierarchy = await hierarchyResponse.json();
        const assignments = await assignmentsResponse.json();

        const accounts = flattenAccountTree(hierarchy.accounts_tree || []);
        const assignmentByAccount = {};
        assignments.forEach(assignment => {
            assignmentByAccount[assignment.account_id] = assignment;
        });

        modal.innerHTML = `
            <div class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
                <div class="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                    <div class="px-6 py-4 border-b border-gray-200">
                        <h3 class="text-lg font-medium text-gray-900">${t('alarmTemplates.manageAccounts')}</h3>
                        <p class="mt-1 text-sm text-gray-500">${t('alarmTemplates.manageAccountsHelp')}</p>
                    </div>
                    <div class="px-6 py-4 space-y-1">
                        ${accounts.length === 0 ? `
                            <p class="text-sm text-gray-500">${t('accounts.noAccountsAvailableShort')}</p>
                        ` : accounts.map(account => {
                            const assignment = assignmentByAccount[account.id];
                            return `
                                <div class="flex items-center justify-between py-2 border-b border-gray-100" style="padding-left: ${account.level * 20}px">
                                    <label class="flex items-center cursor-pointer flex-1">
                                        <input type="checkbox" ${assignment ? 'checked' : ''}
                                               onchange="window.toggleAccountAlarmAssignment('${templateId}', '${account.id}', this.checked)"
                                               class="rounded border-gray-300 text-primary-600 focus:ring-primary-500">
                                        <span class="ml-2 text-sm text-gray-700">${account.name}</span>
                                    </label>
                                    ${assignment ? `
                                        <button type="button" onclick="window.testAccountAlarmAssignment('${assignment._id}')"
                                                class="ml-3 px-3 py-1 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100">
                                            ${t('alarmTemplates.testNow')}
                                        </button>
                                    ` : ''}
                                </div>
                            `;
                        }).join('')}
                    </div>
                    <div class="px-6 py-4 border-t border-gray-200 flex justify-end">
                        <button type="button" onclick="window.closeModal()" class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                            ${t('alarmTemplates.modal.cancel')}
                        </button>
                    </div>
                </div>
            </div>
        `;
        modal.classList.remove('hidden');

    } catch (error) {
        console.error('Error loading account assignments:', error);
        closeModal();
        showError(error.message);
    }
}

/**
 * Assign or unassign an account-level alarm for one account
 */
export async function toggleAccountAlarmAssignment(templateId, accountId, enabled) {
    try {
        const headers = {
            'Authorization': `Bearer ${auth.getToken()}`,
            'Content-Type': 'application/json'
        };

        if (enabled) {
            const response = await fetch(`${API_BASE_URL}/alarm-templates/account-assignments`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ account_id: accountId, template_id: templateId })
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Failed to assign alarm');
            }
        } else {
            const listResponse = await fetch(
                `${API_BASE_URL}/alarm-templates/account-assignments?template_id=${templateId}&account_id=${accountId}`,
                { headers }
            );
            const assignments = await listResponse.json();
            if (assignments.length > 0) {
                const response = await fetch(
                    `${API_BASE_URL}/alarm-templates/account-assignments/${assignments[0]._id}`,
                    { method: 'DELETE', headers }
                );
                if (!response.ok) {
                    const error = await response.json();
                    throw new Error(error.detail || 'Failed to remove alarm');
                }
            }
        }

        await showAccountAlarmAssignments(templateId);

        if (window.loadAlarmTemplates) {
            await window.loadAlarmTemplates();
        }

    } catch (error) {
        console.error('Error updating account assignment:', error);
        showError(error.message);
    }
}

/**
 * Run an account-level alarm now and show what it would report.
 * If locations are missing, offers to actually send the report email as a test.
 */
export async function testAccountAlarmAssignment(assignmentId) {
    try {
        const response = await fetch(
            `${API_BASE_URL}/alarm-templates/account-assignments/${assignmentId}/test`,
            {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${auth.getToken()}` }
            }
        );

        const result = await response.json();

        if (!response.ok) {
            throw new Error(result.detail || 'Failed to run test');
        }

        const list = result.missing_locations
            .map(location => `• ${location.location_name}`)
            .join('<br>');

        if (result.missing_count === 0) {
            await showAlertDialog({
                title: t('alarmTemplates.testNow'),
                message: `${result.locations_checked} locations checked, all had a check-in in the last ${result.lookback_hours} hours. No email would be sent.`,
                icon: 'success'
            });
            return;
        }

        const sendTestEmail = await showConfirmDialog({
            title: t('alarmTemplates.testNow'),
            message: `${result.missing_count} of ${result.locations_checked} locations had no check-in in the last ${result.lookback_hours} hours:<br><br>${list}<br><br>${t('alarmTemplates.sendTestEmailPrompt')}`,
            confirmText: t('alarmTemplates.sendTestEmail'),
            cancelText: t('alarmTemplates.modal.cancel')
        });

        if (!sendTestEmail) return;

        await sendMissingCheckinTestEmail(assignmentId);

    } catch (error) {
        console.error('Error testing account alarm:', error);
        showError(error.message);
    }
}

/**
 * Re-run the test with send_email=true and report per-recipient success/failure
 */
async function sendMissingCheckinTestEmail(assignmentId) {
    try {
        const response = await fetch(
            `${API_BASE_URL}/alarm-templates/account-assignments/${assignmentId}/test?send_email=true`,
            {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${auth.getToken()}` }
            }
        );

        const result = await response.json();

        if (!response.ok) {
            throw new Error(result.detail || 'Failed to send test email');
        }

        const emailResult = result.email_result;

        if (!emailResult || !emailResult.success) {
            await showAlertDialog({
                title: t('alarmTemplates.sendTestEmail'),
                message: emailResult?.error || t('alarmTemplates.testEmailFailed'),
                icon: 'error'
            });
            return;
        }

        const sentCount = (emailResult.sent_to || []).length;
        await showAlertDialog({
            title: t('alarmTemplates.sendTestEmail'),
            message: emailResult.error
                ? `${t('alarmTemplates.testEmailSentTo')} ${sentCount}. ${emailResult.error}`
                : `${t('alarmTemplates.testEmailSentTo')} ${sentCount}.`,
            icon: emailResult.error ? 'warning' : 'success'
        });

    } catch (error) {
        console.error('Error sending test email:', error);
        showError(error.message);
    }
}

// ============================================================================
// Global Exports (for onclick handlers)
// ============================================================================

window.showCreateAlarmTemplateModal = showCreateAlarmTemplateModal;
window.showEditAlarmTemplateModal = showEditAlarmTemplateModal;
window.deleteAlarmTemplate = deleteAlarmTemplate;
window.addSchedule = addSchedule;
window.removeSchedule = removeSchedule;
window.toggleAlarmFields = toggleAlarmFields;
window.toggleSMSRecipients = toggleSMSRecipients;
window.toggleTempSMSRecipients = toggleTempSMSRecipients;
window.sendTestSMS = sendTestSMS;
window.createAlarmTemplate = createAlarmTemplate;
window.updateAlarmTemplate = updateAlarmTemplate;
window.closeModal = closeModal;
window.showAccountAlarmAssignments = showAccountAlarmAssignments;
window.toggleAccountAlarmAssignment = toggleAccountAlarmAssignment;
window.testAccountAlarmAssignment = testAccountAlarmAssignment;
